"use strict";

import { CONSTANTS } from "~/helpers/constants";
import { loadConsentState } from "./cookie";
import { generateScriptKey, isFromSameHost, isMandatoryScript } from "~/helpers";

const blockedScripts = new Map<
  string,
  {
    originalSrc?: string;
    originalContent?: string | null;
    originalType?: string;
    scriptElement: HTMLScriptElement;
    category: string;
  }
>();

/**
 * Initialize the script blocker to prevent script execution
 */
export function initScriptBlocker() {
  blockExistingScripts();
  observeNewScripts();
  overrideCreateElement();
  handleBeforeScriptExecute();
}

/**
 * Block all existing script elements on the page
 */
function blockExistingScripts() {
  const existingScripts = document.querySelectorAll(`script:not(${CONSTANTS.DEFAULT.SCRIPT_ID})`);
  const savedConsent = loadConsentState();

  for (let i = 0; i < existingScripts.length; i++) {
    const node = existingScripts[i] as HTMLScriptElement;

    if (node.id === CONSTANTS.DEFAULT.SCRIPT_NAME || isMandatoryScript(node)) continue;

    const category = node.getAttribute(CONSTANTS.DEFAULT.DATASET_NAME);
    const scriptCategory = category || CONSTANTS.CATEGORIES.UNKNOWN;

    if (savedConsent?.choices?.[scriptCategory] === true) {
      continue;
    }

    const originalSrc = node.src;
    const originalContent = node.textContent;
    const originalType = node.type || CONSTANTS.TEXT_TYPE_ATTRIBUTE;

    const uniqueKey = generateScriptKey("script", i);

    blockedScripts.set(uniqueKey, {
      originalSrc,
      originalContent,
      originalType,
      scriptElement: node,
      category: scriptCategory,
    });

    node.setAttribute("data-original-type", originalType);

    node.type = CONSTANTS.BLOCKED_TYPE_ATTRIBUTE;

    if (originalSrc) {
      node.setAttribute("data-original-src", originalSrc);
    }

    if (category) {
      node.setAttribute(CONSTANTS.DEFAULT.DATASET_NAME, category);
    } else {
      node.setAttribute(CONSTANTS.DEFAULT.DATASET_NAME, CONSTANTS.CATEGORIES.UNKNOWN);
    }

    if (originalContent) {
      node.setAttribute("data-original-content", originalContent);
    }
    node.textContent = `/* Script blocked until consent is given for: ${scriptCategory} */`;
  }
}

/**
 * Set up a MutationObserver to monitor and block new script elements
 */
function observeNewScripts() {
  let debounceTimeout: number | null = null;
  const scriptsQueue: Element[] = [];

  const processQueue = () => {
    if (scriptsQueue.length > 0) {
      processScripts(scriptsQueue.slice());
      scriptsQueue.length = 0;
    }
  };

  const observer = new MutationObserver((mutations) => {
    let hasNewScripts = false;

    for (let i = 0; i < mutations.length; i++) {
      const { addedNodes } = mutations[i]!;

      for (let j = 0; j < addedNodes.length; j++) {
        const node = addedNodes[j] as Element;

        if (node.nodeType === 1 && node.tagName === "SCRIPT") {
          if (node.id === CONSTANTS.DEFAULT.SCRIPT_NAME) continue;
          scriptsQueue.push(node);
          hasNewScripts = true;
        }
      }
    }

    if (hasNewScripts) {
      if (debounceTimeout !== null) {
        window.clearTimeout(debounceTimeout);
      }

      debounceTimeout = window.setTimeout(processQueue, 50);
    }
  });

  observer.observe(document, {
    childList: true,
    subtree: true,
  });
}

/**
 * Process a batch of script elements
 */
function processScripts(scripts: Element[]) {
  const savedConsent = loadConsentState();

  scripts.forEach((node, index) => {
    const scriptElement = node as HTMLScriptElement;

    if (isMandatoryScript(scriptElement)) return;

    const category = scriptElement.getAttribute(CONSTANTS.DEFAULT.DATASET_NAME);
    const scriptCategory = category || CONSTANTS.CATEGORIES.UNKNOWN;

    if (savedConsent?.choices?.[scriptCategory] === true) {
      return;
    }

    const originalSrc = scriptElement.src;
    const originalContent = node.textContent;
    const originalType = scriptElement.type || CONSTANTS.TEXT_TYPE_ATTRIBUTE;

    const uniqueKey = generateScriptKey("dynamic_script", index);

    blockedScripts.set(uniqueKey, {
      originalSrc,
      originalContent,
      originalType,
      scriptElement: scriptElement,
      category: scriptCategory,
    });

    if (originalSrc) {
      scriptElement.setAttribute("data-original-src", originalSrc);
    }
    if (originalContent) {
      scriptElement.setAttribute("data-original-content", originalContent || "");
    }

    scriptElement.setAttribute("data-original-type", originalType);

    scriptElement.type = CONSTANTS.BLOCKED_TYPE_ATTRIBUTE;

    if (!category) {
      scriptElement.setAttribute(CONSTANTS.DEFAULT.DATASET_NAME, CONSTANTS.CATEGORIES.UNKNOWN);
    }

    scriptElement.textContent = `/* Dynamic script blocked until consent is given for: ${scriptCategory} */`;

    const beforeScriptExecuteListener = function (event: Event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      node.removeEventListener("beforescriptexecute", beforeScriptExecuteListener);
    };
    node.addEventListener("beforescriptexecute", beforeScriptExecuteListener);
  });
}

function overrideCreateElement() {
  const createElementBackup = document.createElement;
  const originalDescriptors = {
    src: Object.getOwnPropertyDescriptor(HTMLScriptElement.prototype, "src"),
    type: Object.getOwnPropertyDescriptor(HTMLScriptElement.prototype, "type"),
  };

  document.createElement = function (tagName: string, ...args: any[]) {
    if (tagName.toLowerCase() !== "script") {
      return createElementBackup.call(document, tagName, ...args);
    }

    const scriptElt = createElementBackup.call(document, tagName, ...args) as HTMLScriptElement;

    try {
      const originalSetAttribute = scriptElt.setAttribute;
      scriptElt.setAttribute = function (name: string, value: string) {
        if (name === "id" && value === CONSTANTS.DEFAULT.SCRIPT_NAME) {
          originalSetAttribute.call(this, name, value);
          return;
        }

        if (name === "src" && isFromSameHost(value) && !this.hasAttribute(CONSTANTS.DEFAULT.DATASET_NAME)) {
          originalSetAttribute.call(this, name, value);
          return;
        }

        if (name === "data-cookieconsent") {
          originalSetAttribute.call(this, name, value);

          const savedConsent = loadConsentState();
          if (savedConsent?.choices?.[value] !== true) {
            if (this.type && this.type !== CONSTANTS.BLOCKED_TYPE_ATTRIBUTE) {
              this.setAttribute("data-original-type", this.type);
            }

            if (this.src) {
              this.setAttribute("data-original-src", this.src);
            }

            if (this.textContent) {
              this.setAttribute("data-original-content", this.textContent);
            }

            HTMLScriptElement.prototype.setAttribute.call(this, "type", CONSTANTS.BLOCKED_TYPE_ATTRIBUTE);
            this.textContent = `/* Script blocked until consent is given for: ${value} */`;

            const uniqueKey = generateScriptKey("cookieconsent_script", Date.now());
            blockedScripts.set(uniqueKey, {
              originalSrc: this.src,
              originalContent: this.textContent,
              originalType: this.type || CONSTANTS.TEXT_TYPE_ATTRIBUTE,
              scriptElement: this,
              category: value,
            });
          }
        }

        if (name === CONSTANTS.DEFAULT.DATASET_NAME) {
          originalSetAttribute.call(this, name, value);

          const savedConsent = loadConsentState();
          if (savedConsent?.choices?.[value] === true) {
            return;
          }
        }

        if (this.id !== CONSTANTS.DEFAULT.SCRIPT_NAME && !this.getAttribute(CONSTANTS.DEFAULT.DATASET_NAME)) {
          if (name !== "type" && this.getAttribute(CONSTANTS.DEFAULT.DATASET_NAME) === null && this.type !== CONSTANTS.BLOCKED_TYPE_ATTRIBUTE) {
            this.setAttribute(CONSTANTS.DEFAULT.DATASET_NAME, CONSTANTS.CATEGORIES.UNKNOWN);
          }

          if (this.type && this.type !== CONSTANTS.BLOCKED_TYPE_ATTRIBUTE) {
            this.setAttribute("data-original-type", this.type);
          }

          HTMLScriptElement.prototype.setAttribute.call(this, "type", CONSTANTS.BLOCKED_TYPE_ATTRIBUTE);

          if (this.src) {
            this.setAttribute("data-original-src", this.src);
          }

          if (this.textContent) {
            this.setAttribute("data-original-content", this.textContent);
          }

          const category = this.getAttribute(CONSTANTS.DEFAULT.DATASET_NAME) || CONSTANTS.CATEGORIES.UNKNOWN;
          this.textContent = `/* Script blocked until consent is given for: ${category} */`;
        }

        originalSetAttribute.call(this, name, value);
      };

      if (originalDescriptors.type && originalDescriptors.type.get && originalDescriptors.type.set) {
        Object.defineProperty(scriptElt, "type", {
          get() {
            return originalDescriptors.type!.get!.call(this);
          },
          set(value) {
            if (this.id === CONSTANTS.DEFAULT.SCRIPT_NAME) {
              originalDescriptors.type!.set!.call(this, value);
            } else {
              if (value !== CONSTANTS.BLOCKED_TYPE_ATTRIBUTE) {
                this.setAttribute("data-original-type", value);
              }

              originalDescriptors.type!.set!.call(this, CONSTANTS.BLOCKED_TYPE_ATTRIBUTE);

              if (!this.hasAttribute(CONSTANTS.DEFAULT.DATASET_NAME)) {
                this.setAttribute(CONSTANTS.DEFAULT.DATASET_NAME, CONSTANTS.CATEGORIES.UNKNOWN);
              }
            }
          },
          enumerable: true,
          configurable: true,
        });
      }

      if (originalDescriptors.src && originalDescriptors.src.get && originalDescriptors.src.set) {
        Object.defineProperty(scriptElt, "src", {
          get() {
            return originalDescriptors.src!.get!.call(this);
          },
          set(value) {
            if (this.id === CONSTANTS.DEFAULT.SCRIPT_NAME || (isFromSameHost(value) && !this.hasAttribute(CONSTANTS.DEFAULT.DATASET_NAME))) {
              originalDescriptors.src!.set!.call(this, value);
            } else {
              this.setAttribute("data-original-src", value);
              if (!this.hasAttribute(CONSTANTS.DEFAULT.DATASET_NAME)) {
                this.setAttribute(CONSTANTS.DEFAULT.DATASET_NAME, CONSTANTS.CATEGORIES.UNKNOWN);
              }
            }
          },
          enumerable: true,
          configurable: true,
        });
      }

      if (scriptElt.id !== CONSTANTS.DEFAULT.SCRIPT_NAME && !isMandatoryScript(scriptElt)) {
        if (!scriptElt.hasAttribute(CONSTANTS.DEFAULT.DATASET_NAME)) {
          scriptElt.setAttribute(CONSTANTS.DEFAULT.DATASET_NAME, CONSTANTS.CATEGORIES.UNKNOWN);
        }

        const category = scriptElt.getAttribute(CONSTANTS.DEFAULT.DATASET_NAME) || CONSTANTS.CATEGORIES.UNKNOWN;
        const savedConsent = loadConsentState();
        if (!(savedConsent?.choices?.[category] === true)) {
          scriptElt.type = CONSTANTS.BLOCKED_TYPE_ATTRIBUTE;
        }
      }
    } catch (error) {
      console.warn(
        "Unable to prevent script execution for script src ",
        scriptElt.src,
        ".\n",
        'A likely cause would be because you are using a third-party browser extension that monkey patches the "document.createElement" function.',
      );
    }
    return scriptElt;
  };
}

function handleBeforeScriptExecute() {
  window.addEventListener(
    "beforescriptexecute",
    function (e: Event & { detail?: HTMLScriptElement }) {
      if (!e.detail) return;

      const script = e.detail;
      if (script.id === CONSTANTS.DEFAULT.SCRIPT_NAME || isMandatoryScript(script)) return;

      const savedConsent = loadConsentState();
      const category = script.getAttribute(CONSTANTS.DEFAULT.DATASET_NAME) || CONSTANTS.CATEGORIES.UNKNOWN;

      if (savedConsent?.choices?.[category] === true) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    },
    true,
  );
}

/**
 * Activate scripts for a specific consent category
 * @param category The consent category to activate (analytics, marketing, etc.)
 */
export function activateScriptsForCategory(category: string) {
  let activatedCount = 0;

  const activationList: Array<{
    details: {
      originalSrc?: string;
      originalContent?: string | null;
      originalType?: string;
      scriptElement: HTMLScriptElement;
      category: string;
    };
    key: string;
  }> = [];

  blockedScripts.forEach((details, key) => {
    if (details.category === category) {
      activationList.push({ details, key });
    }
  });

  const activateOne = (item: (typeof activationList)[number]) => {
    return new Promise<void>((resolve) => {
      try {
        const { details } = item;
        const originalScript = details.scriptElement;
        const newScript = originalScript.cloneNode(false) as HTMLScriptElement;

        const originalTypeFromAttribute = originalScript.getAttribute("data-original-type");
        const typeToUse = originalTypeFromAttribute || details.originalType || CONSTANTS.TEXT_TYPE_ATTRIBUTE;
        newScript.setAttribute("type", typeToUse);

        const categoryAttr = originalScript.getAttribute(CONSTANTS.DEFAULT.DATASET_NAME) || details.category || CONSTANTS.CATEGORIES.UNKNOWN;
        newScript.setAttribute(CONSTANTS.DEFAULT.DATASET_NAME, categoryAttr);

        newScript.removeAttribute("data-original-type");
        newScript.removeAttribute("data-original-src");
        newScript.removeAttribute("data-original-content");

        // Preserve attributes
        if (originalScript.hasAttribute("nonce")) newScript.setAttribute("nonce", originalScript.getAttribute("nonce") || "");
        if (originalScript.hasAttribute("crossorigin")) newScript.setAttribute("crossorigin", originalScript.getAttribute("crossorigin") || "");
        if (originalScript.referrerPolicy) newScript.referrerPolicy = originalScript.referrerPolicy as any;
        if (originalScript.noModule) newScript.noModule = originalScript.noModule;

        const hadAsync = originalScript.hasAttribute("async");
        const hadDefer = originalScript.hasAttribute("defer");

        if (hadAsync) newScript.setAttribute("async", "");
        if (hadDefer) newScript.setAttribute("defer", "");

        const parent = originalScript.parentNode;

        // External script
        if (details.originalSrc) {
          newScript.setAttribute("src", details.originalSrc);

          // Dynamically inserted scripts are async by default; if the original was parser-blocking
          // (no async/defer), enforce sequential order by disabling async.
          if (!hadAsync && !hadDefer && (newScript as any).async !== undefined) {
            (newScript as any).async = false;
          }

          newScript.addEventListener("load", () => resolve());
          newScript.addEventListener("error", () => resolve());

          if (parent) parent.replaceChild(newScript, originalScript);
          else document.head.appendChild(newScript);
        } else {
          // Inline script
          const cleanContent = (details.originalContent || "").replace(/<\/?script[^>]*>/gi, "");
          newScript.text = cleanContent;
          if (parent) parent.replaceChild(newScript, originalScript);
          else document.head.appendChild(newScript);
          // Inline executes immediately upon insertion
          resolve();
        }
      } catch (error) {
        console.error(`Failed to activate script for ${category}:`, error);
        resolve();
      }
    });
  };

  const run = async () => {
    for (const item of activationList) {
      await activateOne(item);
      blockedScripts.delete(item.key);
      activatedCount++;
    }

    if (activatedCount > 0) {
      try {
        // Fire readystatechange to complete lifecycle for listeners
        const readyChange = new Event("readystatechange");
        (document as any).readyState = (document as any).readyState || "interactive";
        document.dispatchEvent(readyChange);

        window.cookiedock.__allowed_event = "DOMContentLoaded";
        const domContentLoadedEvent = new Event("DOMContentLoaded");
        document.dispatchEvent(domContentLoadedEvent);

        setTimeout(() => {
          window.cookiedock.__allowed_event = "load";
          const loadEvent = new Event("load");
          window.dispatchEvent(loadEvent);
        }, 0);
      } catch (error) {
        console.error("Error dispatching synthetic events after script activation:", error);
      }
    }
  };

  void run();
}
